// app/frontend/pages/Community/Post/New.tsx
import { useState } from "react";
import { Link, router, usePage } from "@inertiajs/react";
import {
  Box,
  Text,
  Card,
  Group,
  Stack,
  Avatar,
  NavLink,
  ScrollArea,
  Divider,
  ActionIcon,
  Drawer,
  Burger,
  Button,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  IconLayoutList,
  IconBell,
  IconVideo,
  IconArrowLeft,
} from "@tabler/icons-react";
import { SEO } from "../../../components/common/SEO";
import { L2KText, L2KH5, L2KCaption } from "../../../components/ui/Typography";

interface CurrentUser {
  id: number;
  name: string;
  email: string;
  avatar_url: string | null;
  admin: boolean;
}

interface Space {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  emoji: string;
  posts_count: number;
  category?: string | null;
}

interface Props {
  space: Space;
  spaces: Space[];
  errors?: string[];
}

interface SidebarContentProps {
  spaces: Space[];
  currentSpaceSlug: string;
  currentUser: CurrentUser;
  onNavClick?: () => void;
}

function SidebarContent({ spaces, currentSpaceSlug, currentUser, onNavClick }: SidebarContentProps) {
  const spacesByCategory: Record<string, Space[]> = {};
  spaces.forEach((space) => {
    const category = space.category || "Other";
    if (!spacesByCategory[category]) {
      spacesByCategory[category] = [];
    }
    spacesByCategory[category].push(space);
  });

  const categoryOrder = ["Welcome", "Community", "Resources", "Other"];

  return (
    <Box
      style={{
        backgroundColor: "#1a1a1a",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box px="md" py="lg" style={{ borderBottom: "1px solid #2a2a2a" }}>
        <Link href="/" style={{ textDecoration: "none" }} onClick={onNavClick}>
          <Text
            size="xl"
            fw={700}
            style={{
              background: "linear-gradient(135deg, #f97316, #ea580c)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Learntokiz
          </Text>
        </Link>
      </Box>

      <ScrollArea flex={1} px="sm" py="md">
        <Stack gap={4}>
          <NavLink
            component={Link}
            href="/videos"
            label="Learn"
            leftSection={<IconVideo size={18} />}
            onClick={onNavClick}
            styles={{
              root: {
                borderRadius: 8,
                fontWeight: 500,
                color: "#e5e5e5",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.05)" },
              },
              label: { color: "#e5e5e5" },
            }}
          />

          <Divider my="sm" color="#2a2a2a" />

          <NavLink
            component={Link}
            href="/community"
            label="Feed"
            leftSection={<IconLayoutList size={18} />}
            onClick={onNavClick}
            styles={{
              root: {
                borderRadius: 8,
                fontWeight: 500,
                color: "#e5e5e5",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.05)" },
              },
              label: { color: "#e5e5e5" },
            }}
          />

          <Divider my="sm" color="#2a2a2a" />

          {categoryOrder.map((category) => {
            const categorySpaces = spacesByCategory[category];
            if (!categorySpaces || categorySpaces.length === 0) return null;

            return (
              <Box key={category} mb="md">
                <Text size="xs" fw={600} c="#737373" tt="uppercase" px="sm" mb="xs">
                  {category}
                </Text>
                {categorySpaces.map((space) => (
                  <NavLink
                    key={space.id}
                    component={Link}
                    href={`/community/spaces/${space.slug}`}
                    label={space.name}
                    leftSection={<Text size="sm">{space.emoji}</Text>}
                    active={space.slug === currentSpaceSlug}
                    onClick={onNavClick}
                    styles={{
                      root: {
                        borderRadius: 8,
                        color: "#e5e5e5",
                        "&[data-active]": {
                          backgroundColor: "rgba(249, 115, 22, 0.15)",
                          color: "#f97316",
                        },
                        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.05)" },
                      },
                      label: { color: "inherit" },
                    }}
                  />
                ))}
              </Box>
            );
          })}
        </Stack>
      </ScrollArea>

      <Box px="md" py="md" style={{ borderTop: "1px solid #2a2a2a" }}>
        <Group>
          <Avatar src={currentUser.avatar_url} alt={currentUser.name} radius="xl" size="sm" color="orange">
            {currentUser.name.charAt(0)}
          </Avatar>
          <Box flex={1} style={{ minWidth: 0 }}>
            <L2KText size="xs" style={{ fontWeight: 500 }}>
              {currentUser.name}
            </L2KText>
          </Box>
        </Group>
      </Box>
    </Box>
  );
}

export default function NewPost({ space, spaces, errors }: Props) {
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const isDesktop = useMediaQuery("(min-width: 992px)");
  const { current_user } = usePage<{ current_user: CurrentUser }>().props;
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    router.post(
      `/community/spaces/${space.slug}/posts`,
      { post: { title, body } },
      {
        onFinish: () => setIsLoading(false),
      }
    );
  };

  return (
    <Box
      style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse at 20% 50%, rgba(255, 152, 0, 0.15) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 20%, rgba(255, 193, 7, 0.12) 0%, transparent 50%),
          radial-gradient(ellipse at 40% 80%, rgba(255, 87, 34, 0.1) 0%, transparent 50%),
          linear-gradient(135deg, #171717 0%, #1a1a1a 25%, #171717 50%, #1f1f1f 75%, #171717 100%)
        `,
      }}
    >
      <SEO title={`New Post - ${space.name} - Learntokiz`} />

      {/* Mobile Drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        size={280}
        padding={0}
        withCloseButton={false}
        styles={{
          body: { padding: 0, height: "100%" },
          content: { backgroundColor: "#1a1a1a" },
        }}
      >
        <SidebarContent
          spaces={spaces}
          currentSpaceSlug={space.slug}
          currentUser={current_user}
          onNavClick={closeDrawer}
        />
      </Drawer>

      {/* Desktop Sidebar */}
      {isDesktop && (
        <Box
          style={{
            width: 280,
            position: "fixed",
            left: 0,
            top: 0,
            height: "100vh",
            borderRight: "1px solid #2a2a2a",
            zIndex: 100,
          }}
        >
          <SidebarContent spaces={spaces} currentSpaceSlug={space.slug} currentUser={current_user} />
        </Box>
      )}

      {/* Mobile Header */}
      {!isDesktop && (
        <Box
          px="md"
          py="sm"
          style={{
            backgroundColor: "#1a1a1a",
            borderBottom: "1px solid #2a2a2a",
            position: "sticky",
            top: 0,
            zIndex: 50,
          }}
        >
          <Group justify="space-between" align="center">
            <Group gap="sm">
              <Burger opened={drawerOpened} onClick={openDrawer} size="sm" color="#e5e5e5" />
              <Link href="/" style={{ textDecoration: "none" }}>
                <Text
                  size="lg"
                  fw={700}
                  style={{
                    background: "linear-gradient(135deg, #f97316, #ea580c)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Learntokiz
                </Text>
              </Link>
            </Group>
            <ActionIcon variant="subtle" color="gray" size="lg" style={{ color: "#a3a3a3" }}>
              <IconBell size={20} />
            </ActionIcon>
          </Group>
        </Box>
      )}

      {/* Main Content */}
      <Box
        style={{
          marginLeft: isDesktop ? 280 : 0,
          padding: isDesktop ? "2rem" : "1rem",
          maxWidth: isDesktop ? 800 : "100%",
        }}
      >
        <Stack gap="lg">
          {/* Breadcrumb */}
          <Link href={`/community/spaces/${space.slug}`} style={{ textDecoration: "none" }}>
            <Group gap="xs">
              <IconArrowLeft size={isDesktop ? 16 : 14} color="#B8B8B8" />
              <L2KCaption variant="tertiary">
                {space.emoji} {space.name}
              </L2KCaption>
            </Group>
          </Link>

          {/* Form Card */}
          <Card
            padding={isDesktop ? "xl" : "md"}
            radius="md"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              backdropFilter: "blur(10px)",
            }}
          >
            <form onSubmit={handleSubmit}>
              <Stack gap="md">
                <L2KH5>
                  Create a new post in {space.emoji} {space.name}
                </L2KH5>

                {errors && errors.length > 0 && (
                  <L2KText size="xs" style={{ color: "#fa5252" }}>
                    {errors.join(", ")}
                  </L2KText>
                )}

                <TextInput
                  label="Title"
                  placeholder="What's on your mind?"
                  value={title}
                  onChange={(e) => setTitle(e.currentTarget.value)}
                  required
                  styles={{
                    input: {
                      backgroundColor: "rgba(255, 255, 255, 0.03)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      color: "#e5e5e5",
                      fontSize: "14px",
                      "&:focus": {
                        borderColor: "rgba(255, 152, 0, 0.5)",
                      },
                    },
                    label: {
                      color: "#B8B8B8",
                      fontSize: "13px",
                    },
                  }}
                />

                <Textarea
                  label="Body"
                  placeholder="Share your thoughts, questions, or progress..."
                  value={body}
                  onChange={(e) => setBody(e.currentTarget.value)}
                  required
                  minRows={isDesktop ? 8 : 6}
                  autosize
                  styles={{
                    input: {
                      backgroundColor: "rgba(255, 255, 255, 0.03)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      color: "#e5e5e5",
                      fontSize: "14px",
                      "&:focus": {
                        borderColor: "rgba(255, 152, 0, 0.5)",
                      },
                    },
                    label: {
                      color: "#B8B8B8",
                      fontSize: "13px",
                    },
                  }}
                />

                <L2KCaption variant="tertiary">
                  Tip: You can @mention other members and paste YouTube/Vimeo
                  links to embed videos.
                </L2KCaption>

                <Group justify="flex-end" mt="md" gap="sm">
                  <Link href={`/community/spaces/${space.slug}`}>
                    <Button variant="subtle" color="gray" size={isDesktop ? "sm" : "xs"}>
                      Cancel
                    </Button>
                  </Link>
                  <Button
                    type="submit"
                    loading={isLoading}
                    disabled={!title.trim() || !body.trim()}
                    size={isDesktop ? "sm" : "xs"}
                    styles={{
                      root: {
                        background: (!title.trim() || !body.trim()) ? "rgba(255, 255, 255, 0.08)" : "linear-gradient(135deg, #f97316, #ea580c)",
                        border: (!title.trim() || !body.trim()) ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
                        color: (!title.trim() || !body.trim()) ? "#555" : "#fff",
                        "&:disabled": {
                          background: "rgba(255, 255, 255, 0.08)",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          color: "#555",
                        },
                      },
                    }}
                  >
                    Create Post
                  </Button>
                </Group>
              </Stack>
            </form>
          </Card>
        </Stack>
      </Box>
    </Box>
  );
}
